<template>
  <PageContainer>
    <div class="flex flex-row place-content-around justify-between align-center gap-2">
      <div class="flex flex-col place-content-top text-left pt-4">
        <div class="">
          <span class="font-medium text-3xl mr-2">{{ formatPercentage(averageQuality) }}</span>
          <span>daily average score</span>
        </div>
        <div v-if="averageQuality >= 0.85" class="text-gray-800">Your sleep efficiency is optimal!</div>
        <div v-else-if="averageQuality >= 0.80" class="text-gray-800">Aim for an optimimal level of 85% sleep efficiency.</div>
        <div v-else class="text-gray-800">Aim for a good level of 80% sleep efficiency.</div>
      </div>
      <MetricSleepQuality
        class="cursor-pointer"
        @click="infoQuality = true"
        :dateFrom='dateFrom'
        :dateTo='dateTo'
        :hideLabel='true'
        :hideName='true'
      />
    </div>
    <div class="relative w-full h-72 mt-2">
      <div v-if="loading" class="absolute flex flex-col items-center justify-center top-0 -bottom-12 w-full bg-white z-10">
        <object class="h-8 w-auto pointer-events-none animate-ping" :data="logoImage" type="image/svg+xml"></object>
        <p class="mt-4 text-gray-400">Loading...</p>
      </div>
      <TimeseriesGraph
        v-if="graphEfficiency"
        :dataset='dataQuality'
        :stepSize='0.2'
        :formatter='formatPercentage'
        :targetLevels='[0.85]'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if="graphStageREM"
        :dataset='dataStageREM'
        :stepSize='30'
        :formatter='formatDuration'
        :targetLevels='[60*2]'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if="graphStageDeep"
        :dataset='dataStageDeep'
        :stepSize='30'
        :formatter='formatDuration'
        :targetLevels='[60]'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if="graphStageCore"
        :dataset='dataStageCore'
        :stepSize='30'
        :formatter='formatDuration'
        :targetLevels='[60*4]'
        :dateFormat='dateFormat'
      />
      <TimeseriesGraph
        v-else-if="graphStageAwake"
        :dataset='dataStageAwake'
        :stepSize='30'
        :formatter='formatDuration'
        :dateFormat='dateFormat'
      />
    </div>
    <!-- TODO: Yearly graph view. -->
    <div class="grid grid-cols-3 gap-2 w-full bg-gray-50 rounded-lg mt-2">
      <div @click="setRangeWeekly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeWeekly }">7d</div>
      <div @click="setRangeMonthly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeMonthly }">4w</div>
      <div @click="setRangeHalfly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeHalfly }">6m</div>
      <!-- <div @click="setRangeYearly()" class="px-3 py-1 rounded-lg cursor-pointer border-[thin] border-transparent" :class="{ 'bg-teal-100/50 border': rangeYearly }">1y</div> -->
    </div>
    <!-- TODO: On-hover for the buttons. -->
    <div class="-mx-4 overflow-x-auto">
      <div class="flex flex-row gap-2 w-full mt-3 mb-8 mx-4 w-max">
        <div @click="setGraphEfficiency()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphEfficiency }">Efficiency</div>
        <div @click="setGraphStageREM()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphStageREM }">REM</div>
        <div @click="setGraphStageDeep()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphStageDeep }">Deep</div>
        <div @click="setGraphStageCore()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphStageCore }">Core</div>
        <div @click="setGraphStageAwake()" class="rounded-lg border px-3 py-1 text-sm cursor-pointer" :class="{ 'bg-teal-100/50': graphStageAwake }">Awake</div>
      </div>
    </div>
    <div class="mt-4 text-left">
      <h2>Efficiency</h2>
      <div class="grid grid-cols-2 gap-4 justify-between">
        <div @click="infoQuality = true" class="flex flex-col border-2 border-teal-100 rounded-lg p-2 cursor-pointer">
          <div class="text-xl font-medium min-h-[28px]">{{ formatPercentage(averageQuality) }}</div>
          <div>Average efficiency</div>
        </div>
        <div @click="infoQuality = true" class="flex flex-col justify-center bg-theme-teal-bg border-2 border-teal-100 rounded-lg p-2 cursor-pointer">
          <div v-if="averageQuality >= 0.85" class="px-2">Your sleep efficiency is optimal!</div>
          <div v-else-if="averageQuality >= 0.80" class="text-xl font-medium">85%</div>
          <div v-else class="text-xl font-medium">80%</div>
          <div v-if="averageQuality < 0.85">Target efficiency</div>
        </div>
      </div>
    </div>

  <!-- MODALS -->
  <InformationQuality
    v-if="infoQuality"
    v-model="infoQuality"
  />
  </PageContainer>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import PageContainer from '@/components/PageContainer'
import MetricSleepQuality from '@/components/MetricSleepQuality'
import TimeseriesGraph from '@/components/TimeseriesGraph'
import { isSameDate, formatDuration } from '@/helpers/time'
import { mapStores } from 'pinia'
import { useUserStore } from '@/stores/user'

const GRAPH_EFFICIENCY = 0
const GRAPH_STAGE_REM = 1
const GRAPH_STAGE_DEEP = 2
const GRAPH_STAGE_CORE = 3
const GRAPH_STAGE_AWAKE = 4

const RANGE_DAYS_7 = 7 - 1
const RANGE_WEEKS_4 = 7 * 4 - 1
const RANGE_MONTHS_6 = 7 * 4 * 6 - 1
const RANGE_YEARS_1 = 367 - 1

export default {
  name: 'DataQuality',
  components: {
    PageContainer,
    MetricSleepQuality,
    TimeseriesGraph,
    InformationQuality: defineAsyncComponent(
      () => import('@/components/modals/InformationQuality'),
    ),
  },
  data() {
    let d = new Date()
    d.setDate(d.getDate() - RANGE_DAYS_7)

    return {
      graph: GRAPH_EFFICIENCY,
      range: RANGE_DAYS_7,
      loading: true,
      dateFrom: d.format(),
      dateTo: new Date().format(),
      dataQuality: undefined,
      dataStageREM: undefined,
      dataStageDeep: undefined,
      dataStageCore: undefined,
      dataStageAwake: undefined,
      dataLength: undefined,  // Stores data length before padding.
      logoImage: require('@/assets/logo.svg'),
      infoQuality: false,
    }
  },
  async beforeMount() {
    this.loadSleeps()
    this.loadTarget()
  },
  watch: {
    // TODO: Also watch `dateTo` when implementing date range picker.
    dateFrom() {
      this.loadSleeps()
    },
  },
  methods: {
    async loadSleeps() {
      this.loading = true
      const sleeps = await this.userStore.user.getSleeps(this.dateFrom, this.dateTo, true)
      let dataQuality = []
      let dataStageREM = []
      let dataStageDeep = []
      let dataStageCore = []
      let dataStageAwake = []
      for (const sleep of sleeps) {
        let score = await sleep.quality
        dataQuality.push({
          date: sleep.date,
          datapoint: score,
        })
        dataStageREM.push({
          date: sleep.date,
          datapoint: sleep.breakdown.summary.minutesREM,
        })
        dataStageDeep.push({
          date: sleep.date,
          datapoint: sleep.breakdown.summary.minutesDeep,
        })
        dataStageCore.push({
          date: sleep.date,
          datapoint: sleep.breakdown.summary.minutesCore,
        })
        dataStageAwake.push({
          date: sleep.date,
          datapoint: sleep.breakdown.summary.minutesAwake,
        })
      }
      // Pad array so its length is a week.
      this.dataLength = dataQuality.length
      dataQuality = this.padArray(dataQuality, this.range + 1)
      dataStageREM = this.padArray(dataStageREM, this.range + 1)
      dataStageDeep = this.padArray(dataStageDeep, this.range + 1)
      dataStageCore = this.padArray(dataStageCore, this.range + 1)
      dataStageAwake = this.padArray(dataStageAwake, this.range + 1)

      if (dataQuality.length > RANGE_WEEKS_4 + 1) {
        dataQuality = this.monthlyAggregate(dataQuality)
        dataStageREM = this.monthlyAggregate(dataStageREM)
        dataStageDeep = this.monthlyAggregate(dataStageDeep)
        dataStageCore = this.monthlyAggregate(dataStageCore)
        dataStageAwake = this.monthlyAggregate(dataStageAwake)
      }

      // TODO: Sort by date.
      this.dataQuality = dataQuality.sort()
      this.dataStageREM = dataStageREM.sort()
      this.dataStageDeep = dataStageDeep.sort()
      this.dataStageCore = dataStageCore.sort()
      this.dataStageAwake = dataStageAwake.sort()

      this.loading = false
    },
    async loadTarget() {
      // TODO: Set optimal, good, ok, poor levels.
    },
    padArray(arr, len) {
      if (arr.length === len) {
        return arr
      }

      // Pad array with date but empty datapoint field.
      let d = new Date(this.dateFrom)

      // Start with an empty date array.
      let empty = Array(len).fill({})
      const fill = empty.map((element) => {
        element = {
          date: new Date(d),
          datapoint: null
        }
        d.setDate(d.getDate() + 1)
        return element
      })

      // Go through existing datapoints and fill where date matches.
      return fill.map((element) => {
        const match = arr?.find(e => isSameDate(e.date, element.date))
        if (match) {
          return {...element, ...match}
        } else {
          return element
        }
      })
    },
    monthlyAggregate(arr) {
      let result = []
      const reverse = arr.toReversed()
      for (let i = 0; i < reverse.length; i++) {
        let month = []
        const currentMonth = reverse[reverse.length - 1]?.date.getMonth()
        while (currentMonth === reverse[reverse.length - 1]?.date.getMonth()) {
          month.push(reverse.pop())
        }
        result.push({
          date: month[0].date,
          datapoint: this.average(month.map(d => d.datapoint)),
        })
      }
      return result
    },
    formatPercentage(value) {
      return (!value && value != 0) ? '' : Math.floor(value * 100) + '%'
    },
    formatDuration(value) {
      return value === null || isNaN(value) ? '' : formatDuration(value / 60)
    },
    average(array) {
      const nonNullLength = array?.filter(x => x != null).length
      if (array?.length === 0 || nonNullLength === 0) return null
      return array?.reduce((a, b) => a + b) / nonNullLength
    },
    setGraphEfficiency() {
      this.graph = GRAPH_EFFICIENCY
    },
    setGraphStageREM() {
      this.graph = GRAPH_STAGE_REM
    },
    setGraphStageDeep() {
      this.graph = GRAPH_STAGE_DEEP
    },
    setGraphStageCore() {
      this.graph = GRAPH_STAGE_CORE
    },
    setGraphStageAwake() {
      this.graph = GRAPH_STAGE_AWAKE
    },
    setRangeWeekly() {
      this.range = RANGE_DAYS_7
      let d = new Date()
      d.setDate(d.getDate() - RANGE_DAYS_7)
      this.dateFrom = d.format()
    },
    setRangeMonthly() {
      this.range = RANGE_WEEKS_4
      let d = new Date()
      d.setDate(d.getDate() - RANGE_WEEKS_4)
      this.dateFrom = d.format()
    },
    setRangeHalfly() {
      this.range = RANGE_MONTHS_6
      let d = new Date()
      d.setDate(d.getDate() - RANGE_MONTHS_6)
      this.dateFrom = d.format()
    },
    setRangeYearly() {
      this.range = RANGE_YEARS_1
      let d = new Date()
      d.setDate(d.getDate() - RANGE_YEARS_1)
      this.dateFrom = d.format()
    },
  },
  computed: {
    graphEfficiency() {
      return this.graph === GRAPH_EFFICIENCY
    },
    graphStageREM() {
      return this.graph === GRAPH_STAGE_REM
    },
    graphStageDeep() {
      return this.graph === GRAPH_STAGE_DEEP
    },
    graphStageCore() {
      return this.graph === GRAPH_STAGE_CORE
    },
    graphStageAwake() {
      return this.graph === GRAPH_STAGE_AWAKE
    },
    averageQuality() {
      if (this.dataQuality?.length === 0) {
        return null
      }
      let datapoints = this.dataQuality?.map((x) => x.datapoint)
      return this.average(datapoints)
    },
    rangeWeekly() {
      return this.range === RANGE_DAYS_7
    },
    rangeMonthly() {
      return this.range === RANGE_WEEKS_4
    },
    rangeHalfly() {
      return this.range === RANGE_MONTHS_6
    },
    rangeYearly() {
      return this.range === RANGE_YEARS_1
    },
    dateFormat() {
      switch (this.range) {
        case RANGE_DAYS_7:
          return { weekday: 'short' }
        case RANGE_WEEKS_4:
          return { month: 'short', day: 'numeric' }
        case RANGE_MONTHS_6:
          return { month: 'short' }
        case RANGE_YEARS_1:
          return { month: 'short' }
        default:
          return null
      }
    },
    ...mapStores(useUserStore),
  },
}
</script>